import {
  faMagnifyingGlass,
  faUserPlus,
  faUsers,
  faCalendarCheck,
  faPhone,
  faIdCard,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../components/ConfirmationDialog';
import NotificationModal from '../components/NotificationModal';
import StatsCard from '../components/StatsCard';
import usePacientes from '../hooks/usePacientes';
import Button from '../components/Button';
import Input from '../components/Input';
import PageHeader from '../components/PageHeader';
import IconButton from '../components/IconButton';

interface Paciente {
  id: string;
  nome: string;
  celular?: string;
  convenio?: string;
  consultas?: number;
}

interface NotificationState {
  type: 'success' | 'error';
  title: string;
  message: string;
}

export default function Pacientes() {
  const navigate = useNavigate();
  const {
    pacientes,
    paciente,
    totalPages,
    totalPacientes,
    totalConsultas,
    loading,
    error,
    get,
    getStats,
    deleteById
  } = usePacientes();
  const [page, setPage] = useState<number>(0);
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [deletePacienteId, setDeletePacienteId] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationState | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    get(page, 10, debouncedQuery || undefined);
    getStats(debouncedQuery || undefined);
  }, [get, getStats, page, paciente, debouncedQuery]);

  const handleDeletePaciente = async (pacienteId: string): Promise<void> => {
    try {
      await deleteById(pacienteId);
      setDeletePacienteId(null);
      setNotification({
        type: 'success',
        title: 'Paciente Removido',
        message: 'O paciente foi removido com sucesso do sistema.'
      });
      await get(page, 10, debouncedQuery || undefined);
      await getStats(debouncedQuery || undefined);
    } catch (error) {
      setDeletePacienteId(null);
      setNotification({
        type: 'error',
        title: 'Erro ao Remover Paciente',
        message: (error as Error).message || 'Ocorreu um erro inesperado. Tente novamente.'
      });
    }
  };

  function handleAddClick(): void {
    navigate('/pacientes/create');
  }

  function handlePageClick(selectedPage: number): void {
    if (selectedPage < 0 || selectedPage >= totalPages) return;
    setPage(selectedPage);
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
  };

  if (error) {
    return (
      <main className="w-full flex-grow p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Erro ao carregar pacientes: {error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full flex-grow p-6">
      <div className="mx-auto">
        <PageHeader title="Pacientes" subtitle="Gerenciamento de pacientes da clínica" />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total de Pacientes"
            value={loading ? '...' : totalPacientes}
            icon={faUsers}
            color="blue"
          />
          <StatsCard
            title="Pacientes na Lista"
            value={loading ? '...' : pacientes?.length || 0}
            icon={faIdCard}
            color="green"
          />
          <StatsCard
            title={debouncedQuery ? 'Consultas (Filtro)' : 'Total de Consultas'}
            value={loading ? '...' : totalConsultas}
            icon={faCalendarCheck}
            color="purple"
          />
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <Button
              onClick={handleAddClick}
              variant="primary"
              size="lg"
              fullWidth
              leftIcon={faUserPlus}
            >
              Novo Paciente
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                variant="search"
                type="search"
                placeholder="Buscar por nome do paciente..."
                value={query || ''}
                onChange={handleQueryChange}
                leftIcon={faMagnifyingGlass}
              />
            </div>
          </div>
        </div>

        {/* Pacientes List */}
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Lista de Pacientes</h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <img className="w-12 h-12" src="/loading.svg" alt="loading" />
            </div>
          ) : pacientes && pacientes.length > 0 ? (
            <>
              <div className="divide-y divide-gray-200">
                {(pacientes as Paciente[]).map(paciente => (
                  <div key={paciente.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="bg-blue-100 p-3 rounded-full flex-shrink-0">
                          <FontAwesomeIcon icon={faUsers} className="w-6 h-6 text-blue-600" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-4">
                            <div>
                              <p className="text-lg font-medium text-gray-900 truncate">
                                {paciente.nome}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                {paciente.celular && (
                                  <div className="flex items-center space-x-1">
                                    <FontAwesomeIcon icon={faPhone} className="w-3 h-3" />
                                    <span>{paciente.celular}</span>
                                  </div>
                                )}
                                {paciente.convenio && (
                                  <div className="flex items-center space-x-1">
                                    <FontAwesomeIcon icon={faIdCard} className="w-3 h-3" />
                                    <span>Convênio: {paciente.convenio}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {paciente.consultas !== undefined && (
                              <div className="text-center px-4">
                                <div className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                                  {paciente.consultas} consulta{paciente.consultas !== 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <IconButton
                          onClick={() => navigate(`/pacientes/${paciente.id}`)}
                          icon={faEdit}
                          variant="primary"
                          tooltip="Visualizar/Editar paciente"
                        />
                        <IconButton
                          onClick={() => setDeletePacienteId(paciente.id)}
                          icon={faTrash}
                          variant="danger"
                          tooltip="Remover paciente"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      Página {page + 1} de {totalPages}
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageClick(page - 1)}
                        disabled={page === 0}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageClick(page + 1)}
                        disabled={page >= totalPages - 1}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">
                {query ? 'Nenhum paciente encontrado para a busca' : 'Nenhum paciente cadastrado'}
              </p>
              <Button onClick={handleAddClick} variant="outline">
                Cadastrar primeiro paciente
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {deletePacienteId && (
        <ConfirmationDialog
          title="Remover Paciente"
          message="Tem certeza que deseja remover este paciente? Esta ação não pode ser desfeita e removerá também todas as consultas associadas."
          onConfirm={() => handleDeletePaciente(deletePacienteId)}
          onCancel={() => setDeletePacienteId(null)}
          confirmText="Remover"
          cancelText="Cancelar"
          type="danger"
        />
      )}

      {/* Notification Modal */}
      {notification && (
        <NotificationModal
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </main>
  );
}